import { streamReportingAiAsk } from '~/services/ReportingAiAskService';

import type { Lens } from '~/types/leLens';

import { fencedSqlBlocks } from './buildPlan';
import type { LensTurnEffort } from './buildSession';
import { withUntrusted } from './untrustedText';

/**
 * Transport for the model calls behind Lens dashboard building.
 *
 * Three things live here that used to be spread across every call site.
 *
 * A **lane** carries a session id. The first turn on a lane starts a session and
 * keeps the id the server returns; every later turn on that lane sends it back.
 * The server sets `prompt_cache_key` to the session id, so a lane that is reused
 * reads its system prompt and schema context from cache instead of paying for it
 * again. A call site that opens a new session per turn pays full price every time.
 *
 * A **pool** is a fixed number of lanes. Independent card generations run across
 * the lanes concurrently, so the build is faster, while each lane still gets the
 * cache benefit of being reused. One session cannot be shared by two concurrent
 * turns: the server's session is an ordered conversation, and interleaving two
 * card questions in it would put each card's answer in the other card's context.
 * The pool is therefore lanes-of-sessions, not one session with three callers.
 *
 * A **budget** caps how many model requests one build may issue. It is counted
 * here rather than at each call site so a retry loop that misbehaves cannot spend
 * without limit, and so the cap is one number rather than several.
 */

/** Everything one turn said, plus how it ended. */
export type LensAskResult = {
  /** `lensId` is set when the server already saved the Lens from the prepared spec. */
  preparedBuild?: { planId: string; spec: Lens['spec']; lensId?: string };
  answer: string;
  /** Last SQL candidate, kept for callers with nothing to judge them by. */
  sql: string;
  /** Executed queries first, then queries fenced in the prose. */
  sqlBlocks: Array<string>;
  /** Empty when the turn completed. Otherwise the reason, worded for a reader. */
  streamError: string;
  /** True when the turn stopped because the build was cancelled. */
  aborted: boolean;
  /** True when the budget refused the request; no model call was made. */
  overBudget: boolean;
};

export type LensAskOptions = {
  /** Progress lines for the surface driving this turn. */
  onProgress?: (message: string | null) => void;
  /** Untrusted blocks appended after the instruction, announced as data. */
  untrusted?: ReadonlyArray<string>;
  /** Writing SQL to a settled specification is mechanical; deciding it is not. */
  effort?: LensTurnEffort;
};

export type LensAskLane = {
  /** Stable name for logs and job records. */
  readonly key: string;
  /** The session this lane is reusing, once the server has named one. */
  readonly sessionId: string | null;
  ask(question: string, options?: LensAskOptions): Promise<LensAskResult>;
};

/** A build that spent its budget stops asking rather than failing loudly mid-way. */
export const BUDGET_EXHAUSTED =
  'This build reached its limit on how many questions it may ask. Nothing further was requested. Build a smaller Lens, or add the remaining cards one at a time.';

function emptyResult(overrides: Partial<LensAskResult>): LensAskResult {
  return { answer: '', sql: '', sqlBlocks: [], streamError: '', aborted: false, overBudget: false, ...overrides };
}

export type LensAskPoolOptions = {
  sessionId?: string;
  /** How many lanes run at once. Three keeps the warehouse a tolerable neighbour. */
  concurrency?: number;
  /** Hard cap on model requests for the whole build, retries included. */
  budget?: number;
  /** The Lens every session on this pool is born against, so its cost lands somewhere. */
  lensId?: string;
  signal?: AbortSignal;
};

export const DEFAULT_LENS_ASK_CONCURRENCY = 3;
/** A 12-card build plans once, writes a handful of datasets, builds 12 cards and consolidates once, each with at most one retry. */
export const DEFAULT_LENS_ASK_BUDGET = 48;

export type LensAskPool = {
  /** Lane 0. Stage work that must stay in one conversation runs here. */
  readonly primary: LensAskLane;
  lane(index: number): LensAskLane;
  /** Model requests actually issued, retries included. */
  readonly spent: number;
  readonly budget: number;
  readonly remaining: number;
  /** Session ids in lane order, for the job record. */
  sessionIds(): Array<string | null>;
  /** Runs `worker` over `items`, at most `concurrency` at a time, one lane each. */
  map<TItem, TOut>(
    items: ReadonlyArray<TItem>,
    worker: (item: TItem, lane: LensAskLane, index: number) => Promise<TOut>,
  ): Promise<Array<TOut>>;
};

/** The part of a build pool a stage needs: lane 0 for its own turns, `map` for a wave. */
export type BuildPool = Pick<LensAskPool, 'primary' | 'map'>;

export function createLensAskPool(options: LensAskPoolOptions = {}): LensAskPool {
  const concurrency = Math.max(1, options.concurrency ?? DEFAULT_LENS_ASK_CONCURRENCY);
  const budget = Math.max(1, options.budget ?? DEFAULT_LENS_ASK_BUDGET);
  const signal = options.signal;
  const lensId = options.lensId;
  let spent = 0;

  const sessions: Array<string | null> = new Array(concurrency).fill(null);
  sessions[0] = options.sessionId ?? null;

  const makeLane = (index: number): LensAskLane => ({
    key: `lane-${index}`,
    get sessionId() {
      return sessions[index];
    },
    async ask(question: string, askOptions: LensAskOptions = {}): Promise<LensAskResult> {
      if (signal?.aborted) {
        return emptyResult({ aborted: true });
      }
      if (spent >= budget) {
        return emptyResult({ overBudget: true, streamError: BUDGET_EXHAUSTED });
      }
      spent += 1;

      const prompt = withUntrusted(question, askOptions.untrusted ?? []);

      let answer = '';
      const sqlBlocks: Array<string> = [];
      let streamError = '';
      let preparedBuild: LensAskResult['preparedBuild'];

      try {
        await streamReportingAiAsk(
          {
            question: prompt,
            humanInitiated: false,
            origin: 'lens_build',
            ...(lensId ? { lensId } : {}),
            ...(askOptions.effort ? { effort: askOptions.effort } : {}),
            // The whole point of the lane: an existing session is reused, so the
            // server keeps the same prompt_cache_key and the system prompt and
            // schema context are read from cache rather than re-sent at full price.
            sessionId: sessions[index] ?? undefined,
          },
          {
            onProgress: (message) => askOptions.onProgress?.(message ?? null),
            onSession: (sessionId) => {
              if (sessionId) {
                sessions[index] = sessionId;
              }
            },
            onContent: (block) => {
              if (block.type === 'lens_draft' && block.payload.kind === 'lens_build') {
                preparedBuild = block.payload as unknown as NonNullable<LensAskResult['preparedBuild']>;
                return;
              }
              if (block.type === 'sql') {
                const candidate = block.payload?.content;
                if (typeof candidate === 'string' && candidate.trim() && !sqlBlocks.includes(candidate.trim())) {
                  sqlBlocks.push(candidate.trim());
                }
                return;
              }
              const part = block.payload?.content;
              if (typeof part === 'string') {
                answer += `\n${part}`;
              }
            },
            onDone: (summary) => {
              // `done` carries the session id for a turn that started one; the
              // dedicated `session` event is not guaranteed on every server build.
              if (
                summary &&
                typeof summary === 'object' &&
                typeof (summary as { sessionId?: unknown }).sessionId === 'string'
              ) {
                const id = (summary as { sessionId: string }).sessionId;
                if (id) {
                  sessions[index] = id;
                }
              }
            },
            onStreamError: (message) => {
              streamError = message;
            },
          },
          { signal },
        );
      } catch (streamException) {
        // An abort surfaces here. The signal is checked rather than the message,
        // which may be rewritten before it reaches us.
        if (!signal?.aborted) {
          streamError = streamException instanceof Error ? streamException.message : 'The request failed.';
        }
      }

      if (signal?.aborted) {
        return emptyResult({ answer, aborted: true });
      }

      const candidates = [...sqlBlocks, ...fencedSqlBlocks(answer).filter((block) => !sqlBlocks.includes(block))];
      return {
        answer,
        sql: candidates[candidates.length - 1] ?? '',
        sqlBlocks: candidates,
        streamError,
        aborted: false,
        overBudget: false,
        preparedBuild,
      };
    },
  });

  const lanes = Array.from({ length: concurrency }, (_unused, index) => makeLane(index));

  return {
    primary: lanes[0],
    lane: (index: number) => lanes[Math.abs(index) % lanes.length],
    get spent() {
      return spent;
    },
    budget,
    get remaining() {
      return Math.max(0, budget - spent);
    },
    sessionIds: () => [...sessions],
    async map<TItem, TOut>(
      items: ReadonlyArray<TItem>,
      worker: (item: TItem, lane: LensAskLane, index: number) => Promise<TOut>,
    ): Promise<Array<TOut>> {
      const results: Array<TOut> = new Array(items.length);
      let next = 0;
      const runLane = async (laneIndex: number): Promise<void> => {
        const lane = lanes[laneIndex];
        for (;;) {
          const index = next;
          next += 1;
          if (index >= items.length || signal?.aborted) {
            return;
          }
          results[index] = await worker(items[index], lane, index);
        }
      };
      await Promise.all(lanes.slice(0, Math.min(concurrency, Math.max(items.length, 1))).map((_lane, i) => runLane(i)));
      return results;
    },
  };
}

/**
 * One turn, one repair attempt, then an honest failure.
 *
 * `accept` decides whether an answer is usable. `repair` turns a rejected answer
 * into the correction to send with the second attempt. Neither stage is allowed
 * to dead-end on a single bad model response: if the repair also fails, the
 * better of the two is returned along with the reason, and the caller records a
 * failed step rather than a silently missing one.
 */
export async function askWithRepair<TValue>({
  lane,
  ask,
  accept,
  repair,
  prefer,
  onProgress,
}: {
  lane: LensAskLane;
  ask: (correction: string | undefined, lane: LensAskLane) => Promise<LensAskResult>;
  accept: (result: LensAskResult) => TValue | undefined;
  repair: (result: LensAskResult) => string | undefined;
  /** Chooses between two rejected answers when neither is acceptable. */
  prefer?: (first: LensAskResult, second: LensAskResult) => LensAskResult;
  onProgress?: (message: string | null) => void;
}): Promise<{ value?: TValue; result: LensAskResult; retried: boolean }> {
  const first = await ask(undefined, lane);
  const firstValue = accept(first);
  if (firstValue !== undefined) {
    return { value: firstValue, result: first, retried: false };
  }
  if (first.aborted || first.overBudget) {
    return { result: first, retried: false };
  }

  const correction = repair(first);
  if (!correction) {
    return { result: first, retried: false };
  }

  onProgress?.('Asking again…');
  const second = await ask(correction, lane);
  const secondValue = accept(second);
  if (secondValue !== undefined) {
    return { value: secondValue, result: second, retried: true };
  }
  if (second.aborted || second.overBudget) {
    return { result: second, retried: true };
  }
  // Neither was usable. The better of the two is returned so the failure message
  // names the more informative fault rather than whichever came last.
  const kept = prefer ? prefer(first, second) : first.streamError && !second.streamError ? second : first;
  return { result: kept, retried: true };
}
